import type {
  ValidationOptions,
} from "class-validator/types/decorator/ValidationOptions";
import { IsIn } from "class-validator";
import isISO31661Alpha2, { CountryCodes } from "validator/lib/isISO31661Alpha2";

import { ShippingCountryCode } from "@/primary-models";
import { SHIPPING_REGIONS_BY_COUNTRY } from "./regions";

/** These are all the countries considered valid by the class-validator.
 * Countries are in ISO 3166 alpha 2 format. */
export const COUNTRY_CODES = [ ...CountryCodes ];
/** This type fits all the countries considered valid by the class-validator.
 * Countries are in ISO 3166 alpha 2 format. */
export type TIso3166Alpha2CountryCodes = (typeof COUNTRY_CODES)[number];
/** These are all the countries that are considered as valid options for shipping addresses.
 * Countries are in ISO 3166 alpha 2 format. */

export const VALID_SHIPPING_COUNTRIES = Object.values(ShippingCountryCode);
/** This type fits all the countries that are considered as valid options for shipping addresses.
 * Countries are in ISO 3166 alpha 2 format. */
export type TValidShippingCountryCode =
  (typeof VALID_SHIPPING_COUNTRIES)[number];

/** These are all the countries that are considered as valid options for billing addresses.
 * Countries are in ISO 3166 alpha 2 format. */
export const VALID_BILLING_COUNTRIES: TIso3166Alpha2CountryCodes[] = Object.values(COUNTRY_CODES);
/** This type fits all the countries that are considered as valid options for billing addresses.
 * Countries are in ISO 3166 alpha 2 format. */
export type TValidBillingCountryCode = (typeof VALID_BILLING_COUNTRIES)[number];

/** Ensures that type of given string is {@link TValidShippingCountryCode}.
 * Works by using {@link IsIn} so the normal class-validator errors should be returned. */
export const IsValidShippingCountryCode = (
  validationOptions?: ValidationOptions,
) => IsIn(VALID_SHIPPING_COUNTRIES, validationOptions);

export function isValidShippingCountryCode(
  countryCode: string,
): countryCode is TValidShippingCountryCode {
  return VALID_SHIPPING_COUNTRIES.some((cc) => cc === countryCode);
}

export function isIso3166Alpha2CountryCode(
  countryCode: string,
): countryCode is TIso3166Alpha2CountryCodes {
  return isISO31661Alpha2(countryCode);
}

export function getCountryCodeOrDefault(
  countryCode: string | null | undefined,
  fallback: TIso3166Alpha2CountryCodes = "GB",
): TIso3166Alpha2CountryCodes {
  return countryCode && isIso3166Alpha2CountryCode(countryCode)
    ? countryCode
    : fallback;
}

export function getCountryNameInEnglish(countryCode: string): string {
  return (
    new Intl.DisplayNames([ "en" ], { type: "region" }).of(countryCode) ??
    countryCode
  );
}

export function getCountryNameForLocale(
  countryCode: string,
  locale: string,
): string {
  try {
    return new Intl.DisplayNames([ locale ], { type: "region" })
      .of(countryCode) ?? countryCode;
  } catch {
    return getCountryNameInEnglish(countryCode);
  }
}

export function getBillingCountryOptions(locale: string) {
  const collator = new Intl.Collator(locale);

  return COUNTRY_CODES
    .map((countryCode) => {
      const countryName = getCountryNameForLocale(countryCode, locale);

      return {
        title: countryName,
        value: countryCode,
      };
    })
    .sort((left, right) => collator.compare(left.title, right.title));
}

export const countryNameToCountryCodeMap = Object.fromEntries(
  Object.values(VALID_BILLING_COUNTRIES).map((countryCode) => [
    getCountryNameInEnglish(countryCode),
    countryCode,
  ]),
);

export const VALID_SHIPPING_PROVINCES = [
  ...SHIPPING_REGIONS_BY_COUNTRY.GB.map(({ value }) => value),
];
